import { MultiSelect, MultiSelectChangeEvent } from "primereact/multiselect";
import { VirtualScrollerProps } from "primereact/virtualscroller";
import { ControllerRenderProps, FieldValues } from "react-hook-form";
import { KeyboardEvent, ReactNode, useMemo } from "react";
import styles from "./AppMultiselectDropDown.module.scss";
import { CONTAINS_NO_WHITESPACE } from "./dropdownFilterMatchMode";

interface IAppMultiSelectDropDownProps<TOption> {
  field?: ControllerRenderProps<FieldValues, string>;
  options: TOption[] | undefined;
  hasGroupedOptions?: boolean;
  disabled?: boolean;
  className?: string;
  filter?: boolean;
  filterBy?: string;
  editable?: boolean;
  value?: number[];
  invalid?: boolean;
  defaultValue?: number[];
  includeId?: boolean;
  selectAllOption?: { id: number; name: string };
  // A mutually-exclusive "none" option (e.g. id -1). Selecting it clears every other
  // selection; selecting any concrete option clears it. Works alongside selectAllOption.
  noneOption?: { id: number; name: string };
  emptyMessage?: string;
  display?: "chip" | "comma";
  showPanelHeader?: boolean;
  virtualScrollerOptions?: VirtualScrollerProps;
  optionDisabled?: string;
  /**
   * Renders one option row. Supply this when the default single-line "name (#id)" row is not
   * enough — note that a virtualised list pins every row to `virtualScrollerOptions.itemSize`,
   * so a custom template must render at a fixed height or it will overflow into its neighbours.
   */
  itemTemplate?: (option: TOption) => ReactNode;
  /** Class applied to the overlay panel, which is portalled outside this component's tree. */
  panelClassName?: string;
  /**
   * Values switched out of sight without being dropped (e.g. the "Абонаментна карта" payment
   * method of a studio that hides its plans): they are neither offered nor shown as chips,
   * and every change keeps them where they were. Flat options and the plain mode only — not
   * combined with grouped options or selectAllOption / noneOption.
   */
  hiddenValues?: number[];
  /**
   * Past this many selections the field shows `selectedItemsLabel` instead of one chip per
   * value. Unset means a chip for every value, however many there are.
   */
  maxSelectedLabels?: number;
  /** Text shown past `maxSelectedLabels`; "{0}" is replaced with the count. */
  selectedItemsLabel?: string;
  onChange?: (value: number[] | null) => void;
  onFocus?: () => void;
  onBlur?: () => void;
}

// PrimeReact keeps the focused option index while the panel filter narrows the
// list, so pressing Enter on a filter with no (or fewer) results resolves to an
// out-of-range option and pushes a null into the value. That null renders as an
// empty chip and blows up server-side validation on submit. The single-select
// Dropdown guards against this internally; MultiSelect does not, so we strip
// every empty entry before it ever reaches the form.
const withoutEmptyValues = (values: unknown): number[] =>
  Array.isArray(values)
    ? values.filter(
        (item): item is number =>
          item !== null && item !== undefined && !Number.isNaN(item),
      )
    : [];

export const AppMultiSelectDropDown = <TOption,>({
  field,
  options,
  hasGroupedOptions = false,
  disabled,
  className,
  filter,
  filterBy = "name,id",
  editable,
  value,
  invalid,
  defaultValue,
  includeId = true,
  selectAllOption,
  noneOption,
  emptyMessage = "Няма намерени опции",
  display = "chip",
  showPanelHeader = true,
  virtualScrollerOptions,
  optionDisabled,
  itemTemplate: customItemTemplate,
  panelClassName,
  hiddenValues,
  maxSelectedLabels,
  selectedItemsLabel,
  onChange,
  onFocus,
  onBlur,
}: IAppMultiSelectDropDownProps<TOption>) => {
  const hasHiddenValues = !!hiddenValues && hiddenValues.length > 0;
  const isHidden = (id: unknown) => !!hiddenValues?.includes(id as number);
  const rawValue: number[] | null | undefined = value ?? field?.value;

  const onChangeHandler = (e: MultiSelectChangeEvent) => {
    const sanitizedValue = withHiddenValuesKept(withoutEmptyValues(e.value));

    if (selectAllOption || noneOption) {
      const allId = selectAllOption?.id;
      const noneId = noneOption?.id;
      const specialIds = [allId, noneId].filter(
        (x): x is number => x !== undefined,
      );

      const currentValue: number[] = withoutEmptyValues(field?.value ?? value);
      const newValue: number[] = sanitizedValue;
      let effectiveValue: number[];

      if (newValue.length === 0) {
        // Never leave the field empty when there is a default → fall back to "All".
        effectiveValue = allId !== undefined ? [allId] : [];
      } else {
        // A special option (All / None) is exclusive: newly selecting one clears the rest.
        const newlyAddedSpecial = specialIds.find(
          (id) => newValue.includes(id) && !currentValue.includes(id),
        );

        if (newlyAddedSpecial !== undefined) {
          effectiveValue = [newlyAddedSpecial];
        } else {
          // Otherwise keep only the concrete selections, dropping any stale special option
          // (e.g. "Всички" that was still selected when the user picked a specific type).
          const specifics = newValue.filter((x) => !specialIds.includes(x));
          effectiveValue =
            specifics.length > 0
              ? specifics
              : allId !== undefined
                ? [allId]
                : [];
        }
      }

      if (onChange) {
        onChange(effectiveValue);
      } else if (field) {
        field.onChange(effectiveValue);
      }
      return;
    }

    if (onChange) {
      onChange(sanitizedValue.length === 0 ? null : sanitizedValue);
      return;
    }

    if (!field) {
      return;
    }

    field.onChange(sanitizedValue.length === 0 ? null : sanitizedValue);
  };

  // The panel only ever sees the visible part; the hidden values are put back in place by
  // withHiddenValuesKept on every change.
  function withHiddenValuesKept(selected: number[]): number[] {
    if (!hasHiddenValues) {
      return selected;
    }

    const previous = withoutEmptyValues(rawValue);

    return [
      ...previous.filter((id) => isHidden(id) || selected.includes(id)),
      ...selected.filter((id) => !previous.includes(id)),
    ];
  }

  const displayedValue = hasHiddenValues
    ? withoutEmptyValues(rawValue).filter((id) => !isHidden(id))
    : rawValue;

  const visibleOptions = hasHiddenValues
    ? (options as { id?: number }[] | undefined)?.filter(
        (option) => !isHidden(option.id),
      )
    : options;

  const effectiveOptions =
    selectAllOption || noneOption
      ? [
          ...(selectAllOption ? [selectAllOption] : []),
          ...(noneOption ? [noneOption] : []),
          ...((visibleOptions as object[]) ?? []),
        ]
      : visibleOptions;

  const itemTemplate = useMemo(
    () =>
      customItemTemplate ??
      ((option: { name: string; id?: number }) => (
        <div className={styles.itemTemplate}>
          {option.name}
          {/* Hide the id for the special All (0) / None (-1) options; only real ids are shown. */}
          {includeId && (option.id ?? 0) > 0 && (
            <span className={styles.itemId}>(#{option.id})</span>
          )}
        </div>
      )),
    [includeId, customItemTemplate],
  );

  // RFID readers send an Enter of their own after every scan, and the scan lands
  // in the panel filter. That Enter has to stay inside the panel: it must not
  // submit the surrounding form, must not reach page-level keyboard handlers and
  // must not close the panel, so the next card can be scanned right away.
  const onFilterEnter = (event: KeyboardEvent<HTMLDivElement>) => {
    const target = event.target as HTMLElement | null;
    const isPanelFilter = target?.classList?.contains("p-multiselect-filter");

    if (event.key !== "Enter" || !isPanelFilter) {
      return;
    }

    event.preventDefault();
    event.stopPropagation();
  };

  return (
    // The panel is rendered through a React portal, so its key events still
    // bubble up this React tree and get caught here before leaving the field.
    <div style={{ display: "contents" }} onKeyDown={onFilterEnter}>
      <MultiSelect
        id={field?.name}
        value={displayedValue}
        onChange={onChangeHandler}
        options={effectiveOptions}
        className={className}
        disabled={disabled}
        optionLabel="name"
        optionValue="id"
        optionDisabled={optionDisabled}
        filter={filter}
        filterBy={filterBy}
        filterMatchMode={CONTAINS_NO_WHITESPACE}
        emptyMessage={emptyMessage}
        emptyFilterMessage={emptyMessage}
        onFocus={onFocus}
        onBlur={onBlur}
        display={display}
        panelHeaderTemplate={showPanelHeader ? undefined : () => <></>}
        virtualScrollerOptions={virtualScrollerOptions}
        panelClassName={panelClassName}
        filterInputAutoFocus={false}
        optionGroupLabel={hasGroupedOptions ? "label" : ""}
        optionGroupChildren={hasGroupedOptions ? "items" : ""}
        itemTemplate={itemTemplate}
        maxSelectedLabels={maxSelectedLabels}
        selectedItemsLabel={selectedItemsLabel}
      />
    </div>
  );
};
